import { useEffect, useState, useCallback } from "react";
import { Stethoscope, Wrench, Plus, Trash2, Loader2, PackageMinus, Save, ClipboardCheck, DollarSign, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Flujo de diagnóstico mecánico + requisición y consumo de refacciones
// (deducción de inventario) + mano de obra, costeo/rentabilidad y control de calidad.
export default function WorkshopWorkOrder({ item, onRefresh }) {
  const [diag, setDiag] = useState(item.diagnostico || "");
  const [causa, setCausa] = useState(item.causa_raiz || "");
  const [parts, setParts] = useState([]);
  const [pick, setPick] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState("");
  const [labor, setLabor] = useState({ mecanico: "", horas: "", tarifa: "" });
  const [costing, setCosting] = useState(null);
  const [qaItems, setQaItems] = useState(item.qa?.items || []);
  const [qaText, setQaText] = useState("");
  const refacciones = item.refacciones || [];
  const pendientes = refacciones.filter((r) => !r.consumido);
  const manoObra = item.mano_obra || [];

  const loadCosting = useCallback(() => { api.get(`/workshop/work-orders/${item.id}/costing`).then((r) => setCosting(r.data)).catch(() => {}); }, [item.id]);
  useEffect(() => { api.get("/workshop/parts").then((r) => setParts(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadCosting(); setQaItems(item.qa?.items || []); }, [item, loadCosting]);
  useEffect(() => { setDiag(item.diagnostico || ""); setCausa(item.causa_raiz || ""); }, [item.id]);

  const saveDiag = async () => {
    setBusy("diag");
    try { await api.post(`/workshop/work-orders/${item.id}/diagnose`, { diagnostico: diag, causa_raiz: causa }); toast.success("Diagnóstico guardado"); onRefresh && onRefresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };

  const addPart = async () => {
    if (!pick) return;
    setBusy("add");
    try {
      await api.post(`/workshop/work-orders/${item.id}/parts`, { parts: [{ part_id: pick, qty: Number(qty) || 1 }] });
      setPick(""); setQty(1); onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };
  const rmPart = async (lineId) => {
    try { await api.delete(`/workshop/work-orders/${item.id}/parts/${lineId}`); onRefresh && onRefresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const consume = async () => {
    setBusy("consume");
    try {
      const { data } = await api.post(`/workshop/work-orders/${item.id}/consume`);
      toast.success(`${data.consumidas} refacciones consumidas · ${money(data.costo_agregado)} · inventario descontado`);
      api.get("/workshop/parts").then((r) => setParts(r.data)).catch(() => {});
      onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };

  const totalReq = refacciones.reduce((a, r) => a + (Number(r.importe) || 0), 0);

  const addLabor = async () => {
    if (!labor.mecanico.trim() || !(Number(labor.horas) > 0)) { toast.error("Indica mecánico y horas"); return; }
    setBusy("labor");
    try {
      await api.post(`/workshop/work-orders/${item.id}/labor`, { mecanico: labor.mecanico.trim(), horas: Number(labor.horas), tarifa: Number(labor.tarifa) || 0 });
      setLabor({ mecanico: "", horas: "", tarifa: "" }); loadCosting(); onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };
  const rmLabor = async (lineId) => {
    try { await api.delete(`/workshop/work-orders/${item.id}/labor/${lineId}`); loadCosting(); onRefresh && onRefresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const toggleQa = (i) => setQaItems((q) => q.map((x, idx) => idx === i ? { ...x, ok: !x.ok } : x));
  const addQa = () => { if (!qaText.trim()) return; setQaItems((q) => [...q, { text: qaText.trim(), ok: false }]); setQaText(""); };
  const saveQA = async (aprobado) => {
    setBusy("qa");
    try {
      await api.post(`/workshop/work-orders/${item.id}/qa`, { items: qaItems, aprobado });
      toast.success(aprobado ? "QA aprobado" : "QA guardado con retrabajo");
      onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-5" data-testid="workshop-workorder">
      {/* Diagnóstico */}
      <div>
        <div className="flex items-center gap-2 overline text-navy/40 mb-2"><Stethoscope className="w-3.5 h-3.5" /> Diagnóstico mecánico</div>
        <textarea value={diag} onChange={(e) => setDiag(e.target.value)} rows={2} placeholder="Hallazgos del diagnóstico…" className="field-sm w-full mb-2" data-testid="wo-diagnostico" />
        <input value={causa} onChange={(e) => setCausa(e.target.value)} placeholder="Causa raíz" className="field-sm w-full mb-2" data-testid="wo-causa" />
        <button onClick={saveDiag} disabled={busy === "diag"} className="btn btn-outline text-sm" data-testid="wo-save-diag">
          {busy === "diag" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar diagnóstico
        </button>
      </div>

      {/* Refacciones */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 overline text-navy/40"><Wrench className="w-3.5 h-3.5" /> Refacciones</div>
          {totalReq > 0 && <span className="text-xs text-navy/50">Total: {money(totalReq)}</span>}
        </div>
        <div className="flex gap-2 mb-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="field-sm flex-1" data-testid="wo-part-select">
            <option value="">Elige refacción…</option>
            {parts.map((p) => <option key={p.id} value={p.id}>{p.numero_parte} · {p.descripcion} · stock {p.stock} · {money(p.costo)}</option>)}
          </select>
          <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-16 field-sm" data-testid="wo-part-qty" />
          <button onClick={addPart} disabled={!pick || busy === "add"} className="btn btn-outline text-sm" data-testid="wo-part-add"><Plus className="w-4 h-4" /></button>
        </div>

        {refacciones.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {refacciones.map((r) => (
              <div key={r.line_id} className={`flex items-center gap-2 text-sm bg-white border rounded-lg px-3 py-2 ${r.consumido ? "border-emerald-200 bg-emerald-50/50" : "border-navy/10"}`} data-testid={`wo-part-line-${r.numero_parte}`}>
                <div className="min-w-0 flex-1"><div className="truncate">{r.descripcion}</div><div className="text-[11px] text-navy/40">{r.numero_parte} · ×{r.qty} · {money(r.importe)}{r.consumido ? " · consumida" : ""}</div></div>
                {r.consumido ? <PackageMinus className="w-4 h-4 text-emerald-500" /> : <button onClick={() => rmPart(r.line_id)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}

        {pendientes.length > 0 && (
          <button onClick={consume} disabled={busy === "consume"} className="btn btn-primary text-sm w-full" data-testid="wo-consume">
            {busy === "consume" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageMinus className="w-4 h-4" />} Consumir {pendientes.length} refacción(es) y descontar inventario
          </button>
        )}
      </div>

      {/* Mano de obra */}
      <div>
        <div className="flex items-center gap-2 overline text-navy/40 mb-2"><Wrench className="w-3.5 h-3.5" /> Mano de obra</div>
        <div className="flex gap-2 mb-2">
          <input value={labor.mecanico} onChange={(e) => setLabor({ ...labor, mecanico: e.target.value })} placeholder="Mecánico" className="field-sm flex-1" data-testid="wo-labor-mec" />
          <input type="number" min="0" step="0.5" value={labor.horas} onChange={(e) => setLabor({ ...labor, horas: e.target.value })} placeholder="Hrs" className="w-16 field-sm" data-testid="wo-labor-hrs" />
          <input type="number" min="0" value={labor.tarifa} onChange={(e) => setLabor({ ...labor, tarifa: e.target.value })} placeholder="$/hr" className="w-20 field-sm" data-testid="wo-labor-rate" />
          <button onClick={addLabor} disabled={busy === "labor"} className="btn btn-outline text-sm" data-testid="wo-labor-add"><Plus className="w-4 h-4" /></button>
        </div>
        {manoObra.map((l) => (
          <div key={l.line_id} className="flex items-center gap-2 text-sm bg-white border border-navy/10 rounded-lg px-3 py-1.5 mb-1" data-testid={`wo-labor-line`}>
            <span className="flex-1 truncate">{l.mecanico} · {l.horas}h × {money(l.tarifa)}</span>
            <span className="font-medium">{money(l.importe)}</span>
            <button onClick={() => rmLabor(l.line_id)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      {/* Costeo / rentabilidad */}
      {costing && (
        <div className="card-surface p-4" data-testid="wo-costing">
          <div className="flex items-center gap-2 overline text-navy/40 mb-3"><DollarSign className="w-3.5 h-3.5" /> Costeo y rentabilidad</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-navy/60"><span>Refacciones (consumidas)</span><span>{money(costing.refacciones)}</span></div>
            <div className="flex justify-between text-navy/60"><span>Mano de obra</span><span>{money(costing.mano_obra)}</span></div>
            <div className="flex justify-between font-medium border-t border-navy/10 pt-1"><span>Costo total</span><span>{money(costing.costo_total)}</span></div>
            <div className="flex justify-between text-navy/60"><span>Cotización al cliente</span><span>{money(costing.cotizacion)}</span></div>
            <div className={`flex justify-between font-medium text-base ${costing.margen >= 0 ? "text-emerald-600" : "text-destructive"}`}><span>Margen ({costing.margen_pct}%)</span><span data-testid="wo-margen">{money(costing.margen)}</span></div>
          </div>
        </div>
      )}

      {/* Control de calidad */}
      <div>
        <div className="flex items-center gap-2 overline text-navy/40 mb-2"><ClipboardCheck className="w-3.5 h-3.5" /> Control de calidad {item.qa_estado ? `· ${item.qa_estado === "aprobado" ? "Aprobado ✓" : "Retrabajo"}` : ""}</div>
        <div className="space-y-1.5 mb-2">
          {qaItems.map((q, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`wo-qa-item-${i}`}>
              <button onClick={() => toggleQa(i)} className="text-violet">{q.ok ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-navy/30" />}</button>
              <span className={`text-sm flex-1 ${q.ok ? "text-navy/50" : ""}`}>{q.text}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mb-2">
          <input value={qaText} onChange={(e) => setQaText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addQa()} placeholder="Punto de inspección…" className="field-sm flex-1" data-testid="wo-qa-input" />
          <button onClick={addQa} className="btn btn-outline text-sm"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2">
          <button onClick={() => saveQA(false)} disabled={busy === "qa"} className="btn btn-outline text-sm flex-1" data-testid="wo-qa-retrabajo">Marcar retrabajo</button>
          <button onClick={() => saveQA(true)} disabled={busy === "qa"} className="btn btn-primary text-sm flex-1" data-testid="wo-qa-aprobar">
            {busy === "qa" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Aprobar QA
          </button>
        </div>
      </div>
    </div>
  );
}
